import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from '../../icons';
import { Button } from '../ui/button';

interface Props {
  children: ReactNode;
  /** Custom fallback UI — when provided, replaces the default error card. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Top-level React error boundary. Catches render errors that escape
 * component boundaries and shows a recoverable UI instead of a blank page.
 *
 * Usage:
 *   <AppErrorBoundary>
 *     <App />
 *   </AppErrorBoundary>
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AppErrorBoundary] render error', error, errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="flex w-full max-w-md flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 text-center">
            <AlertTriangle size={48} className="text-amber-400" />
            <h1 className="text-heading-h7 text-foreground">Something went wrong</h1>
            <p className="text-body-sm-regular text-muted-foreground">
              We encountered an unexpected error. Please try refreshing the page.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <details className="w-full text-left">
                <summary className="cursor-pointer text-caption-regular text-muted-foreground">Error details</summary>
                <pre className="mt-2 max-h-40 overflow-auto rounded-sm bg-muted p-3 text-caption-regular text-red-600">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="mt-2 flex gap-2">
              <Button variant="outline" size="sm" onClick={this.handleReset}>
                <RefreshCw size={14} className="mr-1" />
                Try again
              </Button>
              <Button size="sm" onClick={() => window.location.reload()}>
                Reload page
              </Button>
              <Button variant="ghost" size="sm" onClick={() => (window.location.href = '/')}>
                <Home size={14} className="mr-1" />
                Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
